import { useMemo } from "react";
import { GraphNoticeType } from "./GraphNotice";

interface UseVisualizationStateInput<T> {
  loading: boolean;
  error: unknown;
  data: T | undefined;
  previousData?: T | undefined;
  getDataArray?: (response: T) => unknown[] | undefined;
  /** Total trade value from the data. When provided, distinguishes between
   *  "no records returned" (hasNoData) and "records exist but sum to 0" (hasZeroTrade). */
  totalValue?: number;
}

interface VisualizationState<T> {
  isLoading: boolean;
  hasError: boolean;
  /** Notice type to show when hasError is true — reflects specific error
   *  classifications (e.g. rate limit) so callers get the right inline copy. */
  errorNoticeType: GraphNoticeType | undefined;
  /** True when the query returned an empty result set. */
  hasNoData: boolean;
  /** True when the query returned records but total trade value is 0. */
  hasZeroTrade: boolean;
  hasData: boolean;
  displayData: T | undefined;
  loading: boolean;
}

function extractStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const candidates: unknown[] = [
    error,
    (error as { networkError?: unknown }).networkError,
    (error as { cause?: unknown }).cause,
  ];
  for (const c of candidates) {
    if (c && typeof c === "object" && "statusCode" in c) {
      const code = (c as { statusCode: unknown }).statusCode;
      if (typeof code === "number") return code;
    }
  }
  return undefined;
}

function classifyErrorNoticeType(
  error: unknown,
): GraphNoticeType | undefined {
  const statusCode = extractStatusCode(error);
  if (statusCode === 429) return GraphNoticeType.RateLimit;
  return undefined;
}

const defaultGetDataArray = (d: { data?: unknown[] }) => d?.data;

export function useVisualizationState<T = { data: unknown[] }>({
  loading,
  error,
  data,
  previousData,
  getDataArray = defaultGetDataArray,
  totalValue,
}: UseVisualizationStateInput<T>): VisualizationState<T> {
  return useMemo(() => {
    const displayData = data ?? previousData;
    const dataArray = displayData ? getDataArray(displayData) : undefined;
    const recordCount = dataArray?.length ?? 0;

    const hasError = !!error && !displayData;
    const hasNoData =
      !loading && !hasError && !!displayData && recordCount === 0;
    const hasZeroTrade =
      !loading && !hasError && !hasNoData && !!displayData &&
      recordCount > 0 && totalValue !== undefined && totalValue === 0;
    const hasData =
      !hasError && !hasNoData && !hasZeroTrade && !!displayData && recordCount > 0;
    const isLoading = loading && !displayData;

    const errorNoticeType = hasError
      ? classifyErrorNoticeType(error)
      : undefined;

    return {
      isLoading,
      hasError,
      errorNoticeType,
      hasNoData,
      hasZeroTrade,
      hasData,
      displayData,
      loading,
    };
  }, [loading, error, data, previousData, getDataArray, totalValue]);
}
